import { Injectable } from '@angular/core';
import { Observable, Subject, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiService } from './api.service';

export type SystemMessageAudience = 'admin' | 'specialist' | 'hospital';

export interface SystemMessage {
  _id?: string;
  message: string;
  targetAudiences: SystemMessageAudience[];
  status?: 'live' | 'inactive';
  activeFrom?: string | null;
  activeTo?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

@Injectable({ providedIn: 'root' })
export class SystemMessagesService {
  private readonly liveMessageChanged = new Subject<void>();
  readonly liveMessageChanged$ = this.liveMessageChanged.asObservable();

  constructor(private api: ApiService) {}

  notifyLiveMessageChanged(): void {
    this.liveMessageChanged.next();
  }

  list(page = 1, limit = 20): Observable<any> {
    return this.api.apiRequest('get', 'admin/system-messages', { page, limit });
  }

  create(message: Pick<SystemMessage, 'message' | 'targetAudiences'>): Observable<any> {
    return this.api.apiRequest('post', 'admin/system-messages', message);
  }

  update(id: string, message: Pick<SystemMessage, 'message' | 'targetAudiences'>): Observable<any> {
    return this.api.apiRequest('put', `admin/system-messages/${id}`, message);
  }

  activate(id: string): Observable<any> {
    return this.api.apiRequest('put', `admin/system-messages/${id}/activate`, {});
  }

  deactivate(id: string): Observable<any> {
    return this.api.apiRequest('put', `admin/system-messages/${id}/deactivate`, {});
  }

  delete(id: string): Observable<any> {
    return this.api.apiRequest('delete', `admin/system-messages/${id}`, {});
  }

  getLoginMessage(audience: SystemMessageAudience): Observable<SystemMessage | null> {
    return this.api.apiRequest('get', 'user/system-message/login', { audience }).pipe(
      map(response => response?.data?.data || null),
      catchError(() => of(null))
    );
  }

  getAuthenticatedMessage(): Observable<SystemMessage | null> {
    return this.api.apiRequest('get', 'user/system-message', {}).pipe(
      map(response => response?.data?.data || null),
      catchError(() => of(null))
    );
  }
}
